import { Link } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEOHead from "./SEOHead";
import solarLogo from "@/assets/solar-logo.png?format=webp&w=200";
import osioLogo from "@/assets/osio-logo.webp";
import { useLanguage, useLangPrefix } from "@/i18n/LanguageContext";

interface ProductPageLayoutProps {
  title: string;
  metaDescription: string;
  image: string;
  children: React.ReactNode;
  partner?: "solar" | "osio";
  path: string;
  alternatePath: string;
}

const DOMAIN = "https://www.ku-kaihdin.fi";

const ProductPageLayout = ({ title, metaDescription, image, children, partner = "solar", path, alternatePath }: ProductPageLayoutProps) => {
  const lang = useLanguage();
  const prefix = useLangPrefix();
  const partnerLogo = partner === "solar" ? solarLogo : osioLogo;
  const partnerAlt = partner === "solar" ? "Solar Kaihdin" : "Osio";
  const partnerUrl = partner === "solar" ? "https://solar.fi" : "https://osio.fi";

  const seoTitle = `${title} | Keski-Uudenmaan Kaihdin`;
  const homeName = lang === "en" ? "Home" : "Etusivu";
  const homeUrl = lang === "en" ? "/en/" : "/";

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": homeName,
        "item": `${DOMAIN}${homeUrl}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": title,
        "item": `${DOMAIN}${path}`
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={metaDescription}
        path={path}
        alternatePath={alternatePath}
        lang={lang as "fi" | "en"}
      />
      <Header />
      <main id="main-content">
        <section className="relative h-[50vh] min-h-[400px] flex items-end">
          <img src={image} alt={title} width={1200} height={600} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-primary/20" />
          <div className="relative container pb-10">
            <Link to={prefix || "/"} className="inline-flex items-center gap-1 text-primary-foreground/80 text-sm mb-4 hover:text-primary-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> {lang === "en" ? "Back" : "Takaisin"}
            </Link>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-primary-foreground">{title}</h1>
          </div>
        </section>

        <section className="py-16">
          <div className="container max-w-3xl prose prose-lg">
            {children}
          </div>
        </section>

        <section className="py-12 bg-secondary">
          <div className="container text-center">
            <h2 className="font-display text-2xl font-bold mb-3">
              {lang === "en" ? "Interested? Get in touch!" : "Kiinnostuitko? Ota yhteyttä!"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {lang === "en" ? "We're happy to provide more information and a free estimate." : "Annamme mielellämme lisätietoja ja ilmaisen arvion."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0405839991" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-semibold text-sm hover:opacity-90 transition-opacity">
                <Phone className="w-4 h-4" /> {lang === "en" ? "Call: 040 583 9991" : "Soita: 040 583 9991"}
              </a>
              <Link to={`${prefix}/${lang === "en" ? "#contact" : "#yhteystiedot"}`} className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary px-6 py-3 rounded-md font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-colors">
                {lang === "en" ? "Send a message" : "Lähetä viesti"}
              </Link>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3 text-sm text-muted-foreground">
              <span>{lang === "en" ? "Products:" : "Tuotteet:"}</span>
              <a href={partnerUrl} target="_blank" rel="noopener noreferrer">
                <img src={partnerLogo} alt={partnerAlt} className="h-6" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
};

export default ProductPageLayout;
